/**
 * ABRIR EL DIÁLOGO DE IMPRESIÓN sobre un PDF nuestro.
 *
 * Pedido del dueño (2026-10-27): *"y quiero que después abra la impresora para imprimirlo"*.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────────
 * POR QUÉ UNA PESTAÑA Y NO UN IFRAME OCULTO
 *
 * La primera versión cargaba el PDF en un iframe 0×0 y llamaba `iframe.contentWindow.print()`. En
 * Chrome eso NO imprime el PDF: el visor de PDF embebido no expone un documento imprimible, así
 * que la llamada se cae a imprimir el documento DE ARRIBA. Lo reportó el dueño con la captura del
 * diálogo: una hoja en blanco, el encabezado con el título de la pestaña del claim y el pie con
 * `https://app.claimsproactive.com/claims/2445?tab=finance`. Imprimió la página, no el cheque.
 *
 * El PDF tiene que ser el documento PRINCIPAL de su propia ventana. Ahí sí el visor responde a
 * `print()`. Y de paso la persona VE el cheque antes de mandarlo al papel, que para un instrumento
 * que la empresa firma no es un detalle menor.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────────
 * LA VENTANA SE ABRE DENTRO DEL CLIC, NO DESPUÉS DEL AWAIT
 *
 * Entre apretar "Print check" y tener la URL hay un POST. Si se llama `window.open` DESPUÉS de ese
 * await, ya no hay gesto del usuario y el navegador bloquea la pestaña. Por eso se parte en dos:
 * `abrirVentanaParaImprimir()` se llama sincrónicamente en el handler y devuelve la ventana, y
 * `imprimirEnVentana()` le pone la URL cuando el servidor contestó. Es el mismo patrón que ya usa
 * `Supplements.tsx` para el estimado revisado.
 *
 * Y SIN la opción 'noopener': con ella `window.open` devuelve `null` y queda una pestaña en blanco
 * (ya nos pasó, 2026-10-08). El PDF es del mismo origen; el `opener` se suelta a mano.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────────
 * LO QUE LA WEB NO PUEDE HACER, Y HAY QUE DECIRLO EN VEZ DE FINGIRLO
 *
 * Ninguna página puede fijar la escala, los márgenes, la bandeja ni la impresora: eso vive en el
 * diálogo del sistema. El PDF pide "no escalar" (`/PrintScaling /None`, que pdf-lib escribe en el
 * cheque), pero es solo una PISTA y los visores de navegador la ignoran. El 2026-10-27 la primera
 * impresión real salió con "fit to page" al ~92 % y todo cayó ~18 pt más arriba. Por eso quien
 * llama a esto TIENE que mostrar el recordatorio de imprimir al 100 % junto al botón.
 *
 * Y si el diálogo no llega a abrirse solo, la pestaña igual queda con el cheque a la vista para
 * imprimir a mano. Falla hacia adelante: lo que no puede pasar es que el botón no haga nada.
 */

/** Se llama DENTRO del clic, antes de cualquier `await`. Devuelve null si el navegador la bloqueó. */
export function abrirVentanaParaImprimir(): Window | null {
  const w = window.open('about:blank', '_blank');
  if (w) {
    try { w.opener = null; } catch { /* algunos navegadores no lo dejan tocar */ }
    try {
      // Algo mientras carga: una pestaña en blanco sin explicación parece un error.
      w.document.write('<title>…</title><p style="font:14px system-ui;padding:24px">…</p>');
      w.document.close();
    } catch { /* si no se puede escribir, no importa */ }
  }
  return w;
}

/** Abre la URL en una pestaña nueva sin dejar `opener` vivo. */
export function abrirEnPestana(url: string): void {
  const w = window.open('about:blank', '_blank');
  if (!w) return;
  try { w.opener = null; } catch { /* idem */ }
  w.location.href = url;
}

/**
 * Carga el PDF en la ventana ya abierta y le pide el diálogo de impresión.
 *
 * Devuelve `true` si se pudo pedir el diálogo. `false` significa que la pestaña se bloqueó o se
 * cerró: el PDF se abre igual como se pueda y quien llama debería decirlo.
 */
export function imprimirEnVentana(win: Window | null, url: string): boolean {
  if (!win || win.closed) {
    // Sin ventana no hay print() posible. Al menos que el cheque quede a la vista.
    abrirEnPestana(url);
    return false;
  }
  win.location.href = url;

  let yaPidio = false;
  const pedir = () => {
    if (yaPidio || win.closed) return;
    yaPidio = true;
    try { win.focus(); win.print(); } catch { /* el visor todavía no está listo; queda a la vista */ }
  };
  // El visor de PDF no dispara `load` de forma confiable en todos los navegadores, así que se
  // intenta por los dos caminos y el `yaPidio` evita que salgan dos diálogos.
  try { win.addEventListener('load', pedir, { once: true }); } catch { /* cross-document raro */ }
  window.setTimeout(pedir, 1500);
  return true;
}
